import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { signInWithCustomToken } from 'firebase/auth';
import FunnelShell from './FunnelShell';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../context/AuthProvider';
import { useModal } from '../../context/ModalProvider';
import { useFunnelCart } from '../../context/FunnelCartProvider';
import { auth } from '../../lib/firebase';
import { getPasswordPolicyError, PASSWORD_POLICY_HINT } from '../../lib/passwordPolicy';

const CHECKOUT_DRAFT_STORAGE_KEY = 'cybard.checkout.draft.v1';

const Stage = styled.section`
  min-height: calc(100vh - 180px);
  height: 100vh;
  display: grid;
  place-items: center;
`;

const Panel = styled.section<{ $flat?: boolean }>`
  border: ${({ $flat, theme }) => ($flat ? 'none' : `1px solid ${theme.colors.gray200}`)};
  border-radius: ${({ $flat }) => ($flat ? '0' : '14px')};
  background: ${({ $flat, theme }) => ($flat ? 'transparent' : theme.colors.gray050)};
  padding: ${({ $flat }) => ($flat ? '0' : '1.2rem')};
  display: grid;
  gap: 0.8rem;
  width: min(680px, 100%);
`;

const PrimaryButton = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  width: fit-content;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.85);
    background: rgba(224, 161, 94, 0.08);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryLink = styled(Link)`
  text-decoration: none;
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  width: fit-content;

  &:hover {
    border-color: rgba(224, 161, 94, 0.85);
    background: rgba(224, 161, 94, 0.08);
  }
`;

const Label = styled.label`
  display: grid;
  gap: 0.35rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: rgba(238, 242, 255, 0.82);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 10px;
  padding: 0.65rem 0.72rem;
  font-size: 0.95rem;
  color: #eaf0ff;
  -webkit-text-fill-color: #eaf0ff;
  background: transparent;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, 0.78);
    box-shadow: 0 0 0 1px rgba(224, 161, 94, 0.28);
  }

  &::placeholder {
    color: rgba(238, 242, 255, 0.48);
  }
`;

const ErrorText = styled.p`
  margin: 0;
  color: #d83b3b;
  font-size: 0.85rem;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.16);
  border-top-color: #e0a15e;
  animation: checkout-spin 0.85s linear infinite;
  margin-bottom: 0.2rem;

  @keyframes checkout-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const AccentTitle = styled.h1`
  margin: 0;
  color: #e0a15e;
  font-size: clamp(2rem, 4.2vw, 2.5rem);
  line-height: 1.06;
  letter-spacing: 0.01em;
`;

const LeadText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray100};
  font-size: 1.02rem;
  line-height: 1.45;
`;

const SoftText = styled.p`
  margin: 0;
  color: rgba(238, 242, 255, 0.72);
  font-size: 0.92rem;
  line-height: 1.4;
`;

type CheckoutStatusResponse = {
  status: 'paid' | 'pending' | 'expired' | 'failed' | 'unknown';
  orderId: string | null;
  customerEmail: string | null;
  accountAction: 'create_account' | 'proceed_dashboard' | 'unknown';
  code:
    | 'SESSION_NOT_FOUND'
    | 'AWAITING_STRIPE_CONFIRMATION'
    | 'ACCOUNT_SETUP_REQUIRED'
    | 'PAYMENT_CONFIRMED'
    | 'CHECKOUT_EXPIRED'
    | 'CHECKOUT_FAILED'
    | 'STATUS_UNMAPPED';
  message: string;
};

type CompleteAccountResponse = {
  ok: true;
  mode: 'signed_in' | 'login_required';
  customToken: string | null;
  orderId: string;
  customerEmail: string;
};

type ViewState =
  | 'checking'
  | 'create_account'
  | 'existing_account'
  | 'pending_timeout'
  | 'expired'
  | 'failed'
  | 'unknown'
  | 'error';

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const { user, refreshRole } = useAuth();
  const { open } = useModal();
  const { clearCart } = useFunnelCart();

  const [status, setStatus] = useState<CheckoutStatusResponse | null>(null);
  const [viewState, setViewState] = useState<ViewState>('checking');
  const [attempts, setAttempts] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const authPromptedRef = useRef(false);
  const cartClearedOnSuccessRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setViewState('unknown');
      return;
    }
    let mounted = true;
    let timeoutId: number | null = null;

    const MAX_ATTEMPTS = 24;
    const POLL_MS = 2500;

    const poll = async () => {
      if (!mounted) return;
      try {
        const response = await apiFetch<CheckoutStatusResponse>(
          `/api/funnel/checkout/session/${sessionId}/status`,
        );
        if (!mounted) return;
        setStatus(response);

        if (response.status === 'paid') {
          if (response.accountAction === 'create_account') {
            setViewState('create_account');
          } else {
            setViewState('existing_account');
          }
          return;
        }

        if (response.status === 'unknown') {
          setViewState('unknown');
          return;
        }
        if (response.status === 'expired') {
          setViewState('expired');
          return;
        }
        if (response.status === 'failed') {
          setViewState('failed');
          return;
        }

        setAttempts((prev) => {
          const next = prev + 1;
          setViewState(next >= MAX_ATTEMPTS ? 'pending_timeout' : 'checking');
          timeoutId = window.setTimeout(() => {
            void poll();
          }, POLL_MS);
          return next;
        });
      } catch {
        if (!mounted) return;
        setViewState('error');
      }
    };

    void poll();

    return () => {
      mounted = false;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [sessionId]);

  useEffect(() => {
    if (viewState !== 'existing_account') return;
    if (user) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (!status?.customerEmail || authPromptedRef.current) return;
    authPromptedRef.current = true;
    open('auth', { email: status.customerEmail });
  }, [navigate, open, status?.customerEmail, user, viewState]);

  useEffect(() => {
    if (!sessionId) return;
    if (status?.status !== 'paid') return;
    if (cartClearedOnSuccessRef.current) return;
    clearCart();
    window.localStorage.removeItem(CHECKOUT_DRAFT_STORAGE_KEY);
    cartClearedOnSuccessRef.current = true;
  }, [clearCart, sessionId, status?.status]);

  const passwordPolicyError = useMemo(
    () => getPasswordPolicyError(newPassword),
    [newPassword],
  );
  const canSubmitPassword = useMemo(
    () => !passwordPolicyError && confirmPassword.trim().length > 0 && !submitting,
    [passwordPolicyError, confirmPassword, submitting],
  );

  const handleCompleteAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!sessionId || !canSubmitPassword) return;
    setSubmitError(null);
    setSubmitting(true);

    try {
      if (passwordPolicyError) {
        setSubmitError(passwordPolicyError);
        return;
      }
      if (!confirmPassword.trim()) {
        setSubmitError('Please confirm your password.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setSubmitError('Passwords do not match.');
        return;
      }
      const result = await apiFetch<CompleteAccountResponse>(
        `/api/funnel/checkout/session/${sessionId}/complete-account`,
        {
          method: 'POST',
          body: JSON.stringify({
            newPassword,
            confirmPassword,
          }),
        },
      );

      if (result.mode === 'login_required' || !result.customToken) {
        setViewState('existing_account');
        open('auth', { email: result.customerEmail });
        return;
      }

      await signInWithCustomToken(auth, result.customToken);
      await refreshRole();
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const message = String(err?.message || '').trim();
      setSubmitError(message || 'Unable to complete account setup right now.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleProceedDashboard = () => {
    if (user) {
      navigate('/dashboard', { replace: true });
      return;
    }
    open('auth', { email: status?.customerEmail ?? '' });
  };

  return (
    <FunnelShell>
      <Stage>
        <Panel $flat={viewState === 'create_account'}>
        {viewState === 'checking' ? (
          <>
            <Spinner aria-hidden />
            <h1 style={{ margin: 0 }}>Finalizing your purchase...</h1>
            <p style={{ margin: 0 }}>
              We are waiting for Stripe confirmation and account setup status.
            </p>
            <p style={{ margin: 0 }}>Please keep this page open.</p>
          </>
        ) : null}

        {viewState === 'create_account' ? (
          <>
            <AccentTitle>Welcome in. Your purchase is confirmed.</AccentTitle>
            <LeadText>
              You just unlocked your service plan. Create your password to activate your client
              dashboard.
            </LeadText>
            {status?.customerEmail ? (
              <SoftText>
                Account email: <strong style={{ color: '#eaf0ff' }}>{status.customerEmail}</strong>
              </SoftText>
            ) : null}
            <SoftText>This takes less than a minute.</SoftText>
            <form onSubmit={handleCompleteAccount} style={{ display: 'grid', gap: '0.72rem' }}>
              <Label>
                Password
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder={PASSWORD_POLICY_HINT}
                />
              </Label>
              <Label>
                Confirm password
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Re-enter password"
                />
              </Label>
              {submitError ? <ErrorText>{submitError}</ErrorText> : null}
              <PrimaryButton type="submit" disabled={!canSubmitPassword}>
                {submitting ? 'Creating account...' : 'Create account'}
              </PrimaryButton>
            </form>
          </>
        ) : null}

        {viewState === 'existing_account' ? (
          <>
            <h1 style={{ margin: 0 }}>Payment successful</h1>
            <p style={{ margin: 0 }}>
              Your purchase is confirmed. Continue to dashboard.
            </p>
            <PrimaryButton type="button" onClick={handleProceedDashboard}>
              Proceed to dashboard
            </PrimaryButton>
          </>
        ) : null}

        {viewState === 'pending_timeout' ? (
          <>
            <h1 style={{ margin: 0 }}>Still finalizing</h1>
            <p style={{ margin: 0 }}>
              Payment is still processing in the background. We are still checking automatically.
            </p>
            <p style={{ margin: 0 }}>This can happen while Stripe finalizes subscription setup.</p>
            <p style={{ margin: 0, opacity: 0.75, fontSize: '0.82rem' }}>
              Auto-check cycle: {attempts}
            </p>
          </>
        ) : null}

        {viewState === 'unknown' ? (
          <>
            <h1 style={{ margin: 0 }}>We could not verify this session</h1>
            <p style={{ margin: 0 }}>
              {status?.message ||
                'This checkout session was not found. Contact support if payment completed.'}
            </p>
            <PrimaryLink to="/services">Back to services</PrimaryLink>
          </>
        ) : null}

        {viewState === 'expired' ? (
          <>
            <h1 style={{ margin: 0 }}>Checkout expired</h1>
            <p style={{ margin: 0 }}>
              {status?.message || 'Your checkout session expired before payment completed.'}
            </p>
            <PrimaryLink to="/services">Start checkout again</PrimaryLink>
          </>
        ) : null}

        {viewState === 'failed' ? (
          <>
            <h1 style={{ margin: 0 }}>Checkout failed</h1>
            <p style={{ margin: 0 }}>
              {status?.message || 'We could not complete checkout for this session.'}
            </p>
            <PrimaryLink to="/services">Return to services</PrimaryLink>
          </>
        ) : null}

        {viewState === 'error' ? (
          <>
            <h1 style={{ margin: 0 }}>Verification error</h1>
            <p style={{ margin: 0 }}>
              We hit a temporary error while checking payment status. We will keep retrying in the background.
            </p>
          </>
        ) : null}
        </Panel>
      </Stage>
    </FunnelShell>
  );
}
